import React, { useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Badge,
  Button,
  Menu,
  MenuItem,
  Avatar,
  InputBase,
  alpha,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AuthModal from "../Auth/AuthModal";
import { MessagesSidebar } from "../MessagesSidebar";
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  AccountCircle as AccountIcon,
  ExitToApp as LogoutIcon,
  Add as AddIcon,
  Message as MessageIcon,
  Category as CategoryIcon,
  Work as WorkIcon,
  AutoAwesome as AIWriterIcon,
  Translate as TranslateIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LocalOffer as LocalOfferIcon,
  Group as GroupIcon,
  HelpOutline as HelpOutlineIcon,
  LocationOn as LocationOnIcon,
  ExpandMore as ExpandMoreIcon,
  Diamond as DiamondIcon,
  Devices as DevicesIcon,
  Movie as MovieIcon,
  EmojiEvents as EmojiEventsIcon,
  TempleBuddhist as TraditionIcon,
  SportsSoccer as SportsIcon,
  Clear as ClearIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  MyLocation as MyLocationIcon,
} from "@mui/icons-material";
import MobileSearchPanel from "../Search/MobileSearchPanel";
import MapSearchNew from "../MapSearch/MapSearchNew";
import SearchSuggestions from "../Search/SearchSuggestions";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { useCartStore } from "../../store/cartStore";
import { useUIStore } from "../../store/uiStore";
import { useAppMode } from "../../contexts/AppModeContext";
import { useWebsiteSettings } from "../../contexts/WebsiteSettingsContext";
import { useTranslation } from "../../contexts/TranslationContext";
import { getCachedEmail, cacheEmail } from "../../utils/profileCache";
import { supabase } from "../../lib/supabase";
import MegaMenu, { defaultMegaMenuData } from "./MegaMenu";
import { mockProducts } from "../../data/mockProducts";
import { bigClothingBrands } from "../../data/brands";
import CartDrawer from "../Cart/CartDrawer";
import FavoritesDrawer from "../Favorites/FavoritesDrawer";

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [rightPanelOpen, setRightPanelOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFormRef = useRef<HTMLDivElement>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [placeOptions, setPlaceOptions] = useState<string[]>([]);
  const [placeLoading, setPlaceLoading] = useState(false);
  const placeAbortRef = React.useRef<AbortController | null>(null);

  const { user, isAuthenticated, logout } = useAuthStore();
  const { totalItems } = useCartStore();
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [favoritesDrawerOpen, setFavoritesDrawerOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const {
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    sidebarOpen,
    setSidebarOpen,
    jobApplicationWriterOpen,
    setJobApplicationWriterOpen,
    jobApplicationWriterMinimized,
    setJobApplicationWriterMinimized,
  } = useUIStore();
  const { is_job } = useAppMode();
  const { settings: siteSettings } = useWebsiteSettings();
  const { language, setLanguage, t, autoTranslate, setAutoTranslate } =
    useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [messagesSidebarOpen, setMessagesSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "register">(
    "login",
  );
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [mapSearchOpen, setMapSearchOpen] = useState(false);
  const [mapInitialQuery, setMapInitialQuery] = useState<string>("");
  const [headerSelectedMapLocations, setHeaderSelectedMapLocations] = useState<string[]>([]);

  React.useEffect(() => {
    const update = () => {
      try {
        const root = document.documentElement;
        const v = root.getAttribute('data-right-panel-open');
        const cssOffset = (typeof window !== 'undefined' && window.getComputedStyle)
          ? (window.getComputedStyle(root).getPropertyValue('--right-panel-offset') || '').trim()
          : '';
        const cssOpen = cssOffset !== '' && cssOffset !== '0' && cssOffset !== '0px';
        setRightPanelOpen(v === 'true' || cssOpen);
      } catch {
        setRightPanelOpen(false);
      }
    };
    update();

    const onPanelChanged = (e: any) => {
      const open = Boolean(e?.detail?.open);
      setRightPanelOpen(open);
    };

    // Observe attribute changes as a fallback
    const obs = new MutationObserver(() => update());
    try {
      window.addEventListener('right-panel-open-changed', onPanelChanged as EventListener);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-right-panel-open', 'style'] });
    } catch {
      // ignore
    }

    return () => {
      try {
        window.removeEventListener('right-panel-open-changed', onPanelChanged as EventListener);
      } catch {
        // ignore
      }
      obs.disconnect();
    };
  }, []);

  React.useEffect(() => {
    const onOpenMobileMenu = () => setMobileMenuOpen(true);
    try {
      window.addEventListener('open-mobile-menu', onOpenMobileMenu as EventListener);
    } catch {
      // ignore
    }
    return () => {
      try {
        window.removeEventListener('open-mobile-menu', onOpenMobileMenu as EventListener);
      } catch {
        // ignore
      }
    };
  }, []);

  React.useEffect(() => {
    const onOpenMessages = () => handleMessagesSidebarOpen();
    try {
      window.addEventListener('open-messages-sidebar', onOpenMessages as EventListener);
    } catch {
      // ignore
    }
    return () => {
      try {
        window.removeEventListener('open-messages-sidebar', onOpenMessages as EventListener);
      } catch {
        // ignore
      }
    };
  }, []);

  // Resolve logos; when running CRA dev on port 3000 and PHP at 8000, rewrite relative /uploads paths
  const toPublicUrl = (p?: string | null) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    try {
      const port = window.location.port;
      const isDevDifferentOrigin = port && port !== '8000';
      if (isDevDifferentOrigin && p.startsWith('/')) {
        const base = `${window.location.protocol}//${window.location.hostname}:8000`;
        return base + p;
      }
    } catch {}
    return p;
  };

  // Resolve logos from website settings with localStorage fallback for dev
  const mainLogoRaw = (siteSettings as any)?.logoPath
    || (typeof window !== 'undefined' ? window.localStorage.getItem('branding.logoDataUrl') || undefined : undefined)
    || '/sistasouk.png';
  const wordLogoRaw = (siteSettings as any)?.logoFontPath
    || (typeof window !== 'undefined' ? window.localStorage.getItem('branding.logoFontDataUrl') || undefined : undefined)
    || '/sistasouk_font.png';
  const mainLogo = toPublicUrl(mainLogoRaw);
  const wordLogo = toPublicUrl(wordLogoRaw);

  // Icon map for top-level categories in the mobile Kategorien dialog
  const categoryIcons: Record<string, React.ReactNode> = {
    Designerartikel: <DiamondIcon />,
    Elektronik: <DevicesIcon />,
    Unterhaltung: <MovieIcon />,
    'Hobby- & Sammlerartikel': <EmojiEventsIcon />,
    Traditionell: <TraditionIcon />,
    Sport: <SportsIcon />,
    Home: <HomeIcon />,
  };

  // Build category-based options like "Herren - Kleidung - Jacken & Mäntel"
  const categoryOptions = React.useMemo(() => {
    const set = new Set<string>();
    const data = defaultMegaMenuData as Record<string, any>;
    for (const [topLabel, content] of Object.entries(data)) {
      const collectFromColumns = (cols?: Array<{ title: string; items: Array<{ label: string }> }>) => {
        if (!cols) return;
        for (const col of cols) {
          for (const it of col.items || []) {
            const label = `${topLabel} - ${col.title}${it.label && it.label !== 'Alle anzeigen' ? ` - ${it.label}` : ''}`;
            set.add(label);
          }
        }
      };
      collectFromColumns(content.right);
      if (content.rightByKey) {
        for (const cols of Object.values(content.rightByKey)) {
          collectFromColumns(cols as any);
        }
      }
    }
    return Array.from(set);
  }, []);

  // Build product name options from mockProducts
  const productOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const p of mockProducts) {
      if (p?.name) set.add(p.name);
    }
    return Array.from(set);
  }, []);

  // Build brand options: big list + brands from mock products
  const brandOptions = React.useMemo(() => {
    const set = new Set<string>(bigClothingBrands);
    for (const p of mockProducts) {
      if (p?.brand) set.add(p.brand);
    }
    return Array.from(set);
  }, []);

  // Language menu state
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  // Search scope dropdown state (left of search bar)
  const [scopeAnchorEl, setScopeAnchorEl] = useState<null | HTMLElement>(null);
  const [searchScope, setSearchScope] = useState<
    | "Produkte" | "Angebote" | "Mitglieder" | "Hilfe-Center" | "Nach Ort"
    | "Jobs" | "Unternehmen"
  >("Produkte");

  const didUserEditSearchRef = React.useRef(false);
  const lastAppliedPlaceUrlKeyRef = React.useRef<string>('');

  // Ensure sensible default when switching between shop/job modes
  React.useEffect(() => {
    try {
      const pathname = location.pathname || '';
      const params = new URLSearchParams(location.search || '');
      const wantsMap = params.get('map');
      const placeRaw = params.get('place');
      const place = placeRaw ? decodeURIComponent(String(placeRaw).replace(/\+/g, ' ')) : '';
      const placeNorm = place.trim();

      const isAllListing = pathname === '/all-products' || pathname === '/all-jobs';
      const hasPlaceDeepLink = isAllListing && wantsMap === '1' && !!placeNorm;

      if (hasPlaceDeepLink) {
        setSearchScope('Nach Ort');
        return;
      }
    } catch {
      // ignore
    }

    setSearchScope(is_job ? "Jobs" : "Produkte");
  }, [is_job, location.pathname, location.search]);

  // If the URL deep-links to a map place (e.g. /all-products?map=1&place=Berlin),
  // auto-select the scope tab "Nach Ort" and prefill the input.
  // Important: don't overwrite while the user is typing, and don't react to map URL updates
  // unless the place actually changed.
  React.useEffect(() => {
    try {
      const pathname = location.pathname || '';
      if (pathname !== '/all-products' && pathname !== '/all-jobs') return;

      const params = new URLSearchParams(location.search || '');
      const wantsMap = params.get('map');
      const placeRaw = params.get('place');
      const place = placeRaw ? decodeURIComponent(String(placeRaw).replace(/\+/g, ' ')) : '';
      const placeNorm = place.trim();
      if (wantsMap !== '1' || !placeNorm) return;

      const urlKey = `${pathname}?map=1&place=${placeNorm}`;
      if (lastAppliedPlaceUrlKeyRef.current === urlKey) return;
      lastAppliedPlaceUrlKeyRef.current = urlKey;

      // If the user is actively editing, don't override their input.
      if (didUserEditSearchRef.current) return;

      if (searchScope !== 'Nach Ort') {
        setSearchScope('Nach Ort');
      }
      if ((searchQuery || '').trim() !== placeNorm) {
        setSearchQuery(placeNorm);
      }
    } catch {
      // ignore
    }
  }, [location.pathname, location.search, searchQuery, searchScope, setSearchQuery]);

  const supportedLanguages = ["en", "de", "tr"];

  const languageOptions = [
    { code: "en", label: "English", flag: "🇺🇸", country: "us", supported: true },
    { code: "de", label: "Deutsch", flag: "🇩🇪", country: "de", supported: true },
    { code: "tr", label: "Türkçe", flag: "🇹🇷", country: "tr", supported: true },
    { code: "fr", label: "Français", flag: "🇫🇷", country: "fr", supported: false },
    { code: "es", label: "Español", flag: "🇪🇸", country: "es", supported: false },
    { code: "it", label: "Italiano", flag: "🇮🇹", country: "it", supported: false },
    { code: "pt", label: "Português", flag: "🇵🇹", country: "pt", supported: false },
    { code: "ar", label: "العربية", flag: "🇸🇦", country: "sa", supported: false },
    { code: "hi", label: "हिन्दी", flag: "🇮🇳", country: "in", supported: false },
    { code: "zh", label: "中文", flag: "🇨🇳", country: "cn", supported: false },
  ];

  const renderFlag = (opt: any, size: number, mr: number) => {
    const country = String(opt?.country || '').toLowerCase();

    const commonSx = {
      width: size,
      height: size,
      mr,
      borderRadius: 999,
      display: 'block',
      overflow: 'hidden',
      flexShrink: 0,
    } as const;

    const svgProps = {
      width: size,
      height: size,
      viewBox: '0 0 30 20',
      preserveAspectRatio: 'xMidYMid slice',
    } as any;

    switch (country) {
      case 'de':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#ffce00" />
              <rect width="3" height="1.333" y="0" fill="#dd0000" />
              <rect width="3" height="0.666" y="0" fill="#000" />
            </svg>
          </Box>
        );
      case 'tr':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#e30a17" />
              <circle cx="1.25" cy="1" r="0.55" fill="#fff" />
              <circle cx="1.38" cy="1" r="0.45" fill="#e30a17" />
              <polygon points="1.92,1 2.15,1.08 2.02,0.88 2.17,0.72 1.95,0.76 1.84,0.56 1.82,0.79 1.60,0.83 1.82,0.92" fill="#fff" />
            </svg>
          </Box>
        );
      case 'us':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps}>
              <rect width="30" height="20" fill="#fff" />
              <g fill="#b22234">
                <rect width="30" height="2" y="0" />
                <rect width="30" height="2" y="4" />
                <rect width="30" height="2" y="8" />
                <rect width="30" height="2" y="12" />
                <rect width="30" height="2" y="16" />
              </g>
              <rect width="12" height="10" fill="#3c3b6e" />
              <g fill="#fff">
                <circle cx="2" cy="2" r="0.5" />
                <circle cx="4" cy="2" r="0.5" />
                <circle cx="6" cy="2" r="0.5" />
                <circle cx="8" cy="2" r="0.5" />
                <circle cx="10" cy="2" r="0.5" />
                <circle cx="3" cy="4" r="0.5" />
                <circle cx="5" cy="4" r="0.5" />
                <circle cx="7" cy="4" r="0.5" />
                <circle cx="9" cy="4" r="0.5" />
                <circle cx="2" cy="6" r="0.5" />
                <circle cx="4" cy="6" r="0.5" />
                <circle cx="6" cy="6" r="0.5" />
                <circle cx="8" cy="6" r="0.5" />
                <circle cx="10" cy="6" r="0.5" />
              </g>
            </svg>
          </Box>
        );
      case 'fr':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="1" height="2" x="0" fill="#0055a4" />
              <rect width="1" height="2" x="1" fill="#fff" />
              <rect width="1" height="2" x="2" fill="#ef4135" />
            </svg>
          </Box>
        );
      case 'es':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#c60b1e" />
              <rect width="3" height="1" y="0.5" fill="#ffc400" />
            </svg>
          </Box>
        );
      case 'it':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="1" height="2" x="0" fill="#009246" />
              <rect width="1" height="2" x="1" fill="#fff" />
              <rect width="1" height="2" x="2" fill="#ce2b37" />
            </svg>
          </Box>
        );
      case 'pt':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="1.2" height="2" x="0" fill="#006600" />
              <rect width="1.8" height="2" x="1.2" fill="#ff0000" />
              <circle cx="1.2" cy="1" r="0.35" fill="#ffd700" />
            </svg>
          </Box>
        );
      case 'sa':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#006c35" />
              <rect x="0.55" y="1.25" width="1.9" height="0.18" fill="#fff" />
            </svg>
          </Box>
        );
      case 'in':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#fff" />
              <rect width="3" height="0.666" y="0" fill="#ff9933" />
              <rect width="3" height="0.666" y="1.333" fill="#138808" />
              <circle cx="1.5" cy="1" r="0.18" fill="#000080" />
            </svg>
          </Box>
        );
      case 'cn':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#de2910" />
              <polygon points="0.5,0.3 0.6,0.55 0.85,0.55 0.65,0.7 0.73,0.95 0.5,0.8 0.27,0.95 0.35,0.7 0.15,0.55 0.4,0.55" fill="#ffde00" />
            </svg>
          </Box>
        );
      default:
        return (
          <Box sx={{ ...commonSx, backgroundColor: 'rgba(0,0,0,0.06)' }}>
            <Box sx={{ width: size, height: size }} />
          </Box>
        );
    }
  };

  const handleUseCurrentLocation = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (searchScope !== 'Nach Ort') {
      setSearchScope('Nach Ort');
    }

    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      searchInputRef.current?.focus();
      return;
    }

    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
      );
    }).catch(() => null);
    if (!pos) {
      searchInputRef.current?.focus();
      return;
    }

    const lat = pos.coords.latitude;
    const lng = pos.coords.longitude;

    try {
      const acceptLang = (() => {
        try {
          return (navigator.languages && navigator.languages.length > 0)
            ? navigator.languages[0]
            : (navigator.language || 'en');
        } catch {
          return 'en';
        }
      })();

      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lng))}&zoom=12&addressdetails=1&accept-language=${encodeURIComponent(acceptLang)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('reverse geocode failed');
      const data = await res.json();
      const address = data?.address || {};
      const place =
        address.city ||
        address.town ||
        address.village ||
        address.municipality ||
        address.county ||
        address.state ||
        data?.name ||
        data?.display_name ||
        '';

      const pretty = String(place || '').split(',')[0].trim() || String(data?.display_name || '').split(',')[0].trim();
      didUserEditSearchRef.current = true;
      if (pretty) {
        setSearchQuery(pretty);
        setSuggestionsOpen(true);
      }
      searchInputRef.current?.focus();
    } catch {
      searchInputRef.current?.focus();
    }
  };

  const openLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const closeLanguageMenu = () => setLanguageAnchorEl(null);

  const handleLanguageSelect = (code: string) => {
    if (supportedLanguages.includes(code)) {
      setLanguage(code as any);
    }
    closeLanguageMenu();
  };

  const selectedLanguageOption = React.useMemo(() => {
    return languageOptions.find((o) => o.code === language) || languageOptions[0];
  }, [language, languageOptions]);

  const actionIconSx = {
    width: 40,
    height: 40,
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "linear-gradient(180deg, rgba(2,132,199,0.06) 0%, rgba(2,132,199,0.02) 100%)",
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '&:hover': {
      background: "linear-gradient(180deg, rgba(2,132,199,0.10) 0%, rgba(2,132,199,0.04) 100%)",
      borderColor: 'rgba(2,132,199,0.22)',
      transform: 'translateY(-1px)',
      boxShadow: '0 10px 22px rgba(2,132,199,0.16)',
    },
    transition: 'all 0.18s ease',
  } as const;

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    const userId = String((user as any)?.id || '').trim();
    if (!isAuthenticated || !userId) {
      setUserEmail(null);
      return;
    }

    const cached = getCachedEmail(userId);
    if (cached) {
      setUserEmail(cached);
      return;
    }

    const storeEmail = String((user as any)?.email || '').trim();
    if (storeEmail) {
      setUserEmail(storeEmail);
      cacheEmail(userId, storeEmail);
      return;
    }

    let cancelled = false;
    const fetchEmail = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('email')
          .eq('id', userId)
          .single();
        if (error) throw error;
        const em = String((data as any)?.email || '').trim();
        if (!cancelled) {
          setUserEmail(em || null);
          if (em) cacheEmail(userId, em);
        }
      } catch {
        if (!cancelled) setUserEmail(null);
      }
    };

    fetchEmail();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, (user as any)?.id, (user as any)?.email]);

  const handleMessagesSidebarOpen = () => {
    setMessagesSidebarOpen(true);
  };

  const handleMessagesSidebarClose = () => {
    setMessagesSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    logout();
    handleProfileMenuClose();
    navigate("/");
  };

  const handleDashboardClick = () => {
    navigate("/dashboard");
    handleProfileMenuClose();
  };

  const handleAuthModalOpen = (tab: "login" | "register") => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const handleAIWriterOpen = () => {
    if (jobApplicationWriterMinimized) {
      // If minimized, restore it
      setJobApplicationWriterMinimized(false);
      setJobApplicationWriterOpen(true);
    } else {
      // If not open, open it
      setJobApplicationWriterOpen(true);
    }
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      if (searchScope === "Nach Ort") {
        const path = is_job ? "/all-jobs" : "/all-products";
        navigate(`${path}?map=1&place=${encodeURIComponent(searchQuery)}`);
        addRecentSearch(searchQuery);
        setSuggestionsOpen(false);
        return;
      }
      if (searchScope === 'Mitglieder') {
        navigate(`/members?search=${encodeURIComponent(searchQuery)}`);
        addRecentSearch(searchQuery);
        setSuggestionsOpen(false);
        return;
      }
      if (searchScope === 'Hilfe-Center') {
        navigate(`/kundencenter?search=${encodeURIComponent(searchQuery)}`);
        addRecentSearch(searchQuery);
        setSuggestionsOpen(false);
        return;
      }
      if (searchScope === 'Angebote') {
        navigate(`/all-products?purchaseType=offer&search=${encodeURIComponent(searchQuery)}`);
        addRecentSearch(searchQuery);
        setSuggestionsOpen(false);
        return;
      }
      if (searchScope === 'Unternehmen') {
        navigate(`/companies?search=${encodeURIComponent(searchQuery)}`);
        addRecentSearch(searchQuery);
        setSuggestionsOpen(false);
        return;
      }
      if (searchScope === 'Jobs' || is_job) {
        navigate(`/all-jobs?search=${encodeURIComponent(searchQuery)}`);
        addRecentSearch(searchQuery);
        setSuggestionsOpen(false);
        return;
      }

      navigate(`/all-products?search=${encodeURIComponent(searchQuery)}`);
      addRecentSearch(searchQuery);
      setSuggestionsOpen(false);
    }
  };

  const openScopeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setScopeAnchorEl(event.currentTarget);
  };

  const closeScopeMenu = () => setScopeAnchorEl(null);

  const handleScopeSelect = (val: "Produkte" | "Angebote" | "Mitglieder" | "Hilfe-Center" | "Nach Ort" | "Jobs" | "Unternehmen") => {
    setSearchScope(val);
    closeScopeMenu();
  };

  const getScopeLandingPath = React.useCallback(
    (val: "Produkte" | "Angebote" | "Mitglieder" | "Hilfe-Center" | "Nach Ort" | "Jobs" | "Unternehmen") => {
      switch (val) {
        case 'Produkte':
          return '/all-products';
        case 'Angebote':
          return '/all-products?purchaseType=offer';
        case 'Mitglieder':
          return '/members';
        case 'Hilfe-Center':
          return '/kundencenter';
        case 'Nach Ort':
          return '/all-products?map=1';
        case 'Jobs':
          return '/all-jobs';
        case 'Unternehmen':
          return '/companies';
        default:
          return '/all-products';
      }
    },
    []
  );

  const navigateToScopeLanding = React.useCallback(
    (val: "Produkte" | "Angebote" | "Mitglieder" | "Hilfe-Center" | "Nach Ort" | "Jobs" | "Unternehmen") => {
      const path = getScopeLandingPath(val);
      if (!path) return;
      closeScopeMenu();
      navigate(path);
    },
    [closeScopeMenu, getScopeLandingPath, navigate]
  );

  const searchPlaceholder = (() => {
    if (is_job) return t("header.searchJobs");
    switch (searchScope) {
      case "Produkte":
        return t("header.searchProducts");
      case "Angebote":
        return "In Angeboten suchen...";
      case "Mitglieder":
        return "Mitglieder suchen...";
      case "Hilfe-Center":
        return "Im Hilfe-Center suchen...";
      case "Nach Ort":
        return "Nach Ort suchen...";
      default:
        return t("header.searchProducts");
    }
  })();

  // Debounce the query to drive suggestions component
  React.useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 180);
    return () => clearTimeout(id);
  }, [searchQuery]);

  React.useEffect(() => {
    const q = (debouncedQuery || '').trim();
    if (searchScope !== 'Nach Ort') {
      if (placeAbortRef.current) {
        try { placeAbortRef.current.abort(); } catch { /* ignore */ }
      }
      placeAbortRef.current = null;
      setPlaceLoading(false);
      setPlaceOptions([]);
      return;
    }

    if (!q || q.length < 2) {
      if (placeAbortRef.current) {
        try { placeAbortRef.current.abort(); } catch { /* ignore */ }
      }
      placeAbortRef.current = null;
      setPlaceLoading(false);
      setPlaceOptions([]);
      return;
    }

    if (placeAbortRef.current) {
      try { placeAbortRef.current.abort(); } catch { /* ignore */ }
    }
    const ctrl = new AbortController();
    placeAbortRef.current = ctrl;
    let cancelled = false;

    const run = async () => {
      setPlaceLoading(true);
      try {
        const acceptLang = (() => {
          try {
            return (navigator.languages && navigator.languages.length > 0)
              ? navigator.languages[0]
              : (navigator.language || 'en');
          } catch {
            return 'en';
          }
        })();

        const fetchNominatim = async () => {
          const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=8&addressdetails=1&accept-language=${encodeURIComponent(acceptLang)}`;
          const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
          if (!res.ok) return [] as Array<{ display_name?: string }>;
          const data = (await res.json()) as Array<{ display_name?: string }>;
          return Array.isArray(data) ? data : [];
        };

        const fetchOpenMeteo = async () => {
          const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=8&language=${encodeURIComponent(acceptLang)}&format=json`;
          const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
          if (!res.ok) return [] as Array<{ display_name?: string }>;
          const data = (await res.json()) as any;
          const results = Array.isArray(data?.results) ? data.results : [];
          return results.map((r: any) => ({
            display_name: [r?.name, r?.admin1, r?.country].filter(Boolean).join(', '),
          }));
        };

        let hits: Array<{ display_name?: string }> = [];
        try {
          hits = await fetchNominatim();
        } catch {
          hits = [];
        }
        if (!hits || hits.length === 0) {
          try {
            hits = await fetchOpenMeteo();
          } catch {
            hits = [];
          }
        }

        const out = Array.from(
          new Set(
            (hits || [])
              .map((h) => String(h?.display_name || '').trim())
              .filter(Boolean)
          )
        ).slice(0, 10);

        if (!cancelled) setPlaceOptions(out);
      } catch {
        if (!cancelled) setPlaceOptions([]);
      } finally {
        if (!cancelled) setPlaceLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      try { ctrl.abort(); } catch { /* ignore */ }
    };
  }, [debouncedQuery, searchScope]);

  const openSuggestions = () => setSuggestionsOpen(true);
  const closeSuggestions = () => setSuggestionsOpen(false);

  const handleSuggestionSelect = (term: string) => {
    setSearchQuery(term);
    addRecentSearch(term);
    if (searchScope === 'Nach Ort') {
      const path = is_job ? "/all-jobs" : "/all-products";
      navigate(`${path}?map=1&place=${encodeURIComponent(term)}`);
      setSuggestionsOpen(false);
      return;
    }
    const searchPath = is_job ? "/all-jobs" : "/all-products";
    navigate(`${searchPath}?search=${encodeURIComponent(term)}`);
    setSuggestionsOpen(false);
  };

  const menuItems = [
    // Keep job links only in job mode (products/categories/home removed per request)
    ...(is_job
      ? [
          { label: t("header.jobs"), path: "/all-jobs", icon: <WorkIcon /> },
          {
            label: t("header.jobCategories"),
            path: "/job-categories",
            icon: <WorkIcon />,
          },
          {
            label: t("header.companies"),
            path: "/companies",
            icon: <WorkIcon />,
          },
        ]
      : []),
  ];

  const userMenuItems = [
    { label: t("header.dashboard"), path: "/dashboard", icon: <AccountIcon /> },
    {
      label: t("header.profile"),
      path: "/user-profile",
      icon: <AccountIcon />,
    },
    // Show different menu items based on mode
    ...(is_job
      ? [{ label: t("header.interviews"), path: "/orders", icon: <WorkIcon /> }]
      : [{ label: t("header.orders"), path: "/orders", icon: <StoreIcon /> }]),
    ...(user?.role === "seller"
      ? [
          {
            label: t("header.sellerDashboard"),
            path: "/seller",
            icon: <AddIcon />,
          },
        ]
      : []),
  ];

  const handleMessagesClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleMessagesSidebarOpen();
  };

  const renderMobileMenu = () => (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{
        "& .MuiDrawer-paper": {
          width: 280,
          pt: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {is_job ? (
            <Typography
              variant="h6"
              fontWeight={800}
              color="primary.main"
              sx={{ letterSpacing: 0.2 }}
            >
              JobFinder
            </Typography>
          ) : (
            <>
              <img
                src={mainLogo}
                alt="Sistasouk"
                style={{ height: 28 }}
              />
              <img
                src={wordLogo}
                alt="Sistasouk wordmark"
                style={{ height: 22 }}
              />
            </>
          )}
        </Box>
        <IconButton onClick={() => setMobileMenuOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <List>
        {/* Mobile quick links */}
        <ListItemButton
          onClick={() => {
            navigate("/");
            setMobileMenuOpen(false);
          }}
          sx={{
            mx: 1.5,
            my: 0.75,
            borderRadius: 2.5,
            border: '1px solid rgba(0,0,0,0.08)',
            background:
              'linear-gradient(180deg, rgba(2,132,199,0.06) 0%, rgba(2,132,199,0.02) 100%)',
            '&:hover': {
              background:
                'linear-gradient(180deg, rgba(2,132,199,0.1) 0%, rgba(2,132,199,0.04) 100%)',
              borderColor: 'rgba(2,132,199,0.2)'
            },
          }}
        >
          <ListItemIcon>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary={t("header.home")} />
        </ListItemButton>

        {!is_job && (
          <>
            {/* Browse Categories (mobile-friendly full-screen) */}
            <ListItemButton
              onClick={() => setMobileCategoriesOpen(true)}
              sx={{
                mx: 1.5,
                my: 0.75,
                borderRadius: 2.5,
                border: '1px solid rgba(0,0,0,0.08)',
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
                '&:hover': {
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.03) 100%)'
                },
              }}
            >
              <ListItemIcon>
                <CategoryIcon />
              </ListItemIcon>
              <ListItemText primary={t("header.categories")} />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate("/all-products");
                setMobileMenuOpen(false);
              }}
              sx={{
                mx: 1.5,
                my: 0.75,
                borderRadius: 2.5,
                border: '1px solid rgba(109,40,217,0.18)',
                background:
                  'linear-gradient(180deg, rgba(139,92,246,0.06) 0%, rgba(139,92,246,0.03) 100%)',
                '&:hover': {
                  background:
                    'linear-gradient(180deg, rgba(139,92,246,0.1) 0%, rgba(139,92,246,0.05) 100%)',
                  borderColor: 'rgba(109,40,217,0.28)'
                },
              }}
            >
              <ListItemIcon>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText primary={t("header.products")} />
            </ListItemButton>

            <ListItemButton
              onClick={() => {
                navigate("/categories");
                setMobileMenuOpen(false);
              }}
              sx={{
                mx: 1.5,
                my: 0.75,
                borderRadius: 2.5,
                border: '1px solid rgba(0,0,0,0.08)',
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
                '&:hover': {
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.03) 100%)'
                },
              }}
            >
              <ListItemIcon>
                <CategoryIcon />
              </ListItemIcon>
              <ListItemText primary={t("header.categories")} />
            </ListItemButton>
          </>
        )}

        <Divider sx={{ my: 1 }} />
        {menuItems.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => {
              navigate(item.path);
              setMobileMenuOpen(false);
            }}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>

      {!isAuthenticated && (
        <>
          <Divider />
          <List>
            <ListItemButton
              onClick={() => {
                handleAuthModalOpen("login");
                setMobileMenuOpen(false);
              }}
              sx={{
                mx: 1.5,
                my: 1,
                borderRadius: 2.5,
                border: '1px solid rgba(109,40,217,0.2)',
                background:
                  'linear-gradient(180deg, rgba(139,92,246,0.06) 0%, rgba(139,92,246,0.03) 100%)',
                '&:hover': {
                  background:
                    'linear-gradient(180deg, rgba(139,92,246,0.1) 0%, rgba(139,92,246,0.05) 100%)',
                  borderColor: 'rgba(109,40,217,0.32)'
                },
              }}
            >
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary={t("header.login")} />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                handleAuthModalOpen("register");
                setMobileMenuOpen(false);
              }}
              sx={{
                mx: 1.5,
                my: 1,
                borderRadius: 2.5,
                background:
                  'linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%)',
                color: 'primary.contrastText',
                boxShadow: '0 6px 18px rgba(109,40,217,0.25)',
                '& .MuiListItemIcon-root': { color: 'white' },
                '& .MuiListItemText-primary': { color: 'white', fontWeight: 700 },
                '&:hover': {
                  background:
                    'linear-gradient(90deg, #7c3aed 0%, #5b21b6 100%)',
                  boxShadow: '0 8px 22px rgba(109,40,217,0.32)'
                },
              }}
            >
              <ListItemIcon>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText primary={t("header.signUp")} />
            </ListItemButton>
          </List>
        </>
      )}

      {isAuthenticated && (
        <>
          <Divider />
          <List>
            {/* AI Job Writer - only in job mode */}
            {is_job && (
              <ListItemButton
                onClick={() => {
                  handleAIWriterOpen();
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemIcon>
                  <AIWriterIcon
                    sx={{
                      color: jobApplicationWriterMinimized
                        ? "#10b981"
                        : "#8b5cf6",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    jobApplicationWriterMinimized
                      ? t("header.restoreJobAIWriter")
                      : t("header.jobAIWriter")
                  }
                />
              </ListItemButton>
            )}
            {userMenuItems.map((item) => (
              <ListItemButton
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t("header.logout")} />
            </ListItemButton>
          </List>
        </>
      )}
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "white",
          color: "text.primary",
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          left: 0,
          right: 'auto',
          width: { xs: '100%', md: 'calc(100% - var(--right-panel-offset, 0px))' },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 44, sm: 52 }, py: 0, gap: 1, overflow: rightPanelOpen ? 'hidden' : 'visible' }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              flexGrow: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              flexShrink: 0,
              mr: isMobile ? 2 : (rightPanelOpen ? 1 : 0),
            }}
          >
            {is_job ? (
              <Typography
                variant="h6"
                fontWeight={800}
                color="primary.main"
                sx={{ letterSpacing: 0.2 }}
              >
                JobFinder
              </Typography>
            ) : (
              <>
                <img
                  src={mainLogo}
                  alt="Sistasouk"
                  style={{ height: 28 }}
                />
                <img
                  src={wordLogo}
                  alt="Sistasouk wordmark"
                  style={{ height: 24 }}
                />
              </>
            )}
          </Box>

          {/* Search scope dropdown + Search Bar (Desktop only) - placed directly next to logo */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                minWidth: 0,
                ml: rightPanelOpen ? 1 : '30px',
                mr: rightPanelOpen ? 1 : '30px',
              }}
            >
              <Button
                onClick={openScopeMenu}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  height: 36,
                  px: rightPanelOpen ? 1 : 1.5,
                  color: 'text.primary',
                  border: '1px solid rgba(0,0,0,0.12)',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  minWidth: rightPanelOpen ? 96 : 130,
                  flexShrink: 0,
                }}
              >
                {searchScope}
              </Button>

              <Box
                component="form"
                onSubmit={handleSearch}
                ref={searchFormRef}
                sx={{
                  position: "relative",
                  isolation: 'isolate',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'rgba(124,58,237,0.25)',
                  overflow: 'visible',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: 0.75,
                    borderRadius: 999,
                    background:
                      'linear-gradient(90deg, #22c55e, #06b6d4, #3b82f6, #a855f7, #ec4899, #f97316, #22c55e)',
                    backgroundSize: '300% 100%',
                    opacity: 0,
                    transition: 'opacity 200ms ease',
                    zIndex: 0,
                    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    animation: 'headerSearchGlow 1.6s ease-in-out infinite',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -10,
                    borderRadius: 999,
                    background:
                      'linear-gradient(90deg, rgba(34,197,94,0.45), rgba(6,182,212,0.45), rgba(59,130,246,0.45), rgba(168,85,247,0.45), rgba(236,72,153,0.45), rgba(249,115,22,0.45), rgba(34,197,94,0.45))',
                    backgroundSize: '300% 100%',
                    opacity: 0,
                    filter: 'blur(16px)',
                    transition: 'opacity 220ms ease',
                    zIndex: -1,
                    pointerEvents: 'none',
                    animation: 'headerSearchGlow 1.6s ease-in-out infinite',
                  },
                  '@keyframes headerSearchGlow': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                  '&:focus-within::before': {
                    opacity: 1,
                  },
                  '&:focus-within::after': {
                    opacity: 1,
                  },
                  '&:hover': {
                    borderColor: 'rgba(124,58,237,0.35)'
                  },
                  marginLeft: 0,
                  flex: 1,
                  width: '100%',
                  height: 40,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 1,
                    borderRadius: 999,
                    backgroundColor: '#fff',
                    zIndex: 1,
                    pointerEvents: 'none',
                    boxShadow: '0 1px 0 rgba(0,0,0,0.03) inset',
                  }}
                />
                <Box
                  sx={{
                    padding: theme.spacing(0, 2),
                    height: "100%",
                    position: "absolute",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                  }}
                >
                  <SearchIcon />
                </Box>
                <InputBase
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    didUserEditSearchRef.current = true;
                    setSearchQuery(e.target.value);
                    setSuggestionsOpen(true);
                  }}
                  fullWidth
                  inputRef={searchInputRef}
                  onFocus={openSuggestions}
                  sx={{
                    width: '100%',
                    color: "inherit",
                    position: 'relative',
                    zIndex: 2,
                    "& .MuiInputBase-input": {
                      padding: theme.spacing(0.95, 1, 0.95, 0),
                      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                      paddingRight: (() => {
                        // Space for: (optional clear) + (optional locate) + submit pill
                        // Provide extra room for longer labels like "Mitglieder finden".
                        if (searchScope === 'Nach Ort') return theme.spacing(30);
                        if (searchScope === 'Mitglieder') return theme.spacing(31);
                        if (searchScope === 'Hilfe-Center') return theme.spacing(33);
                        if (searchScope === 'Unternehmen') return theme.spacing(31);
                        if (searchScope === 'Angebote') return theme.spacing(29);
                        if (searchScope === 'Jobs' || is_job) return theme.spacing(28);
                        return theme.spacing(26);
                      })(),
                      transition: theme.transitions.create("width"),
                      width: "100%",
                      fontSize: '0.95rem',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    right: 6,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 3,
                  }}
                >
                  {searchQuery && (
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={(e) => {
                        e.preventDefault();
                        didUserEditSearchRef.current = true;
                        setSearchQuery('');
                        searchInputRef.current?.focus();
                      }}
                      sx={{
                        width: 30,
                        height: 30,
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}

                  {searchScope === 'Nach Ort' && (
                    <IconButton
                      size="small"
                      aria-label="use current location"
                      onClick={handleUseCurrentLocation}
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: 'rgba(255,255,255,0.65)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.85)',
                        },
                      }}
                    >
                      <MyLocationIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}

                  <Button
                    type="submit"
                    disabled={!searchQuery.trim()}
                    sx={{
                      height: 32,
                      borderRadius: 999,
                      px: 2,
                      fontWeight: 900,
                      textTransform: 'none',
                      letterSpacing: 0.2,
                      color: 'white',
                      background: 'linear-gradient(135deg, rgba(124,58,237,1) 0%, rgba(59,130,246,1) 100%)',
                      boxShadow: '0 10px 24px rgba(124,58,237,0.25)',
                      border: '1px solid rgba(255,255,255,0.24)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(139,92,246,1) 0%, rgba(96,165,250,1) 100%)',
                        boxShadow: '0 14px 30px rgba(124,58,237,0.32)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.65)',
                        background: 'linear-gradient(135deg, rgba(107,114,128,0.55) 0%, rgba(55,65,81,0.55) 100%)',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      {(() => {
                        switch (searchScope) {
                          case 'Nach Ort':
                            return <LocationOnIcon sx={{ fontSize: 18 }} />;
                          case 'Angebote':
                            return <LocalOfferIcon sx={{ fontSize: 18 }} />;
                          case 'Mitglieder':
                            return <GroupIcon sx={{ fontSize: 18 }} />;
                          case 'Hilfe-Center':
                            return <HelpOutlineIcon sx={{ fontSize: 18 }} />;
                          case 'Unternehmen':
                            return <WorkIcon sx={{ fontSize: 18 }} />;
                          case 'Jobs':
                            return <WorkIcon sx={{ fontSize: 18 }} />;
                          default:
                            return <SearchIcon sx={{ fontSize: 18 }} />;
                        }
                      })()}
                      {(() => {
                        switch (searchScope) {
                          case 'Nach Ort':
                            return t('header.findLocation') || 'Ort finden';
                          case 'Angebote':
                            return t('header.findOffers') || 'Angebote finden';
                          case 'Mitglieder':
                            return t('header.findMembers') || 'Mitglieder finden';
                          case 'Hilfe-Center':
                            return t('header.findHelp') || 'Hilfe finden';
                          case 'Unternehmen':
                            return t('header.findCompanies') || 'Unternehmen finden';
                          case 'Jobs':
                            return t('header.findJobs') || 'Jobs finden';
                          default:
                            return t('header.findProducts') || 'Produkte finden';
                        }
                      })()}
                    </Box>
                  </Button>
                </Box>
              </Box>
              <SearchSuggestions
                anchorEl={searchFormRef.current}
                open={suggestionsOpen}
                query={debouncedQuery}
                recentSearches={recentSearches || []}
                categoryOptions={categoryOptions}
                productOptions={productOptions}
                brandOptions={brandOptions}
                placeOptions={searchScope === 'Nach Ort' ? placeOptions : []}
                placeLoading={searchScope === 'Nach Ort' ? placeLoading : false}
                onSelect={handleSuggestionSelect}
                onClose={closeSuggestions}
                onClearRecent={clearRecentSearches}
                onRemoveRecent={removeRecentSearch}
              />
            </Box>
          )}

          {/* Desktop Navigation */}
          {!isMobile && menuItems.length > 0 && (
            <Box sx={{ display: rightPanelOpen ? 'none' : "flex", gap: 2, mx: 2 }}>
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    color="inherit"
                    onClick={() => navigate(item.path)}
                    sx={{
                      textTransform: 'none',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.98rem',
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: rightPanelOpen && !isMobile ? 'none' : "flex", alignItems: "center", gap: 1, ml: 'auto' }}>
            <Button
              color="inherit"
              onClick={openLanguageMenu}
              sx={{
                ...actionIconSx,
                minWidth: 'auto',
                px: 1.25,
                gap: 0.75,
                '& .MuiButton-startIcon': { m: 0 },
              }}
              aria-label="change language"
            >
              <Box sx={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                {renderFlag(selectedLanguageOption as any, 18, 0)}
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, lineHeight: 1 }}>
                {String(selectedLanguageOption?.code || language).toUpperCase()}
              </Typography>
            </Button>

            {/* Search icon for mobile */}
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setMobileSearchOpen((s) => !s)}
                sx={{
                  ...actionIconSx,
                }}
                aria-label="mobile search"
              >
                <SearchIcon />
              </IconButton>
            )}

            {/* AI Job Writer - only in job mode */}
            {is_job && (
              <IconButton
                color="inherit"
                onClick={handleAIWriterOpen}
                sx={{
                  ...actionIconSx,
                  background: jobApplicationWriterMinimized
                    ? "linear-gradient(180deg, rgba(16,185,129,0.1) 0%, rgba(16,185,129,0.05) 100%)"
                    : "linear-gradient(180deg, rgba(139,92,246,0.1) 0%, rgba(139,92,246,0.05) 100%)",
                  color: jobApplicationWriterMinimized ? "#10b981" : "#8b5cf6",
                  "&:hover": {
                    background: jobApplicationWriterMinimized
                      ? "linear-gradient(180deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.08) 100%)"
                      : "linear-gradient(180deg, rgba(139,92,246,0.15) 0%, rgba(139,92,246,0.08) 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: jobApplicationWriterMinimized
                      ? '0 10px 22px rgba(16,185,129,0.16)'
                      : '0 10px 22px rgba(139,92,246,0.18)',
                  },
                }}
                title={
                  jobApplicationWriterMinimized
                    ? t("header.restoreJobAIWriter")
                    : t("header.openJobAIWriter")
                }
              >
                <AIWriterIcon />
              </IconButton>
            )}

            {/* Favorites - hidden in job mode; hide on mobile (moved into user menu) */}
            {!isMobile && !is_job && (
              <IconButton
                color="inherit"
                onClick={() => setFavoritesDrawerOpen(true)}
                sx={{
                  ...actionIconSx,
                  color: 'text.secondary',
                  '&:hover': {
                    ...actionIconSx['&:hover'],
                    color: 'primary.main',
                  },
                }}
              >
                <FavoriteIcon />
              </IconButton>
            )}

            {/* Cart - hidden in job mode; hide on mobile (moved into user menu) */}
            {!isMobile && !is_job && (
              <IconButton
                color="inherit"
                onClick={() => setCartDrawerOpen(true)}
                sx={{
                  ...actionIconSx,
                  color: 'text.secondary',
                  '& .MuiBadge-badge': {
                    boxShadow: '0 6px 14px rgba(239,68,68,0.25)',
                  },
                }}
              >
                <Badge badgeContent={totalItems} color="error">
                  <CartIcon />
                </Badge>
              </IconButton>
            )}

            {/* Desktop burger to open navigation drawer */}
            {!isMobile && (
              <IconButton
                color="inherit"
                aria-label="open navigation menu"
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  ...actionIconSx,
                  background: "linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)",
                  '&:hover': {
                    ...actionIconSx['&:hover'],
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.03) 100%)',
                    borderColor: 'rgba(0,0,0,0.18)',
                    boxShadow: '0 10px 22px rgba(0,0,0,0.10)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* User Menu */}
            {isAuthenticated ? (
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="primary-search-account-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{
                  ...actionIconSx,
                }}
              >
                <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.main" }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            ) : (
              !isMobile && (
                <Box sx={{ display: "flex", gap: 1.25 }}>
                  <Button
                    onClick={() => handleAuthModalOpen("login")}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 1.75,
                      height: 34,
                      fontWeight: 600,
                      color: theme.palette.primary.dark,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.07)} 100%)`,
                        borderColor: alpha(theme.palette.primary.main, 0.38),
                      },
                    }}
                  >
                    {t("header.login")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleAuthModalOpen("register")}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 2,
                      height: 34,
                      fontWeight: 700,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                      '&:hover': {
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.34)}`,
                      },
                    }}
                  >
                    {t("header.signUp")}
                  </Button>
                </Box>
              )
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Search Scope Menu */}
      <Menu
        anchorEl={scopeAnchorEl}
        open={Boolean(scopeAnchorEl)}
        onClose={closeScopeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            mt: 1,
            minWidth: 260,
            boxShadow: '0 16px 40px rgba(2,132,199,0.16)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)',
            '& .MuiMenuItem-root': {
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              px: 1.25,
            },
          },
        }}
      >
        {is_job ? (
          <>
            <MenuItem onClick={() => handleScopeSelect('Jobs')} selected={searchScope === 'Jobs'}>
              <ListItemIcon>
                <WorkIcon />
              </ListItemIcon>
              <ListItemText primary="Jobs" />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToScopeLanding('Jobs');
                }}
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(124,58,237,0.18)',
                  background: 'linear-gradient(180deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.04) 100%)',
                  color: '#6d28d9',
                  '&:hover': {
                    background: 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(139,92,246,0.08) 100%)',
                    transform: 'translateX(1px)',
                  },
                  transition: 'all 0.15s ease',
                }}
                aria-label="go to jobs"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={() => handleScopeSelect('Unternehmen')} selected={searchScope === 'Unternehmen'}>
              <ListItemIcon>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText primary="Unternehmen" />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToScopeLanding('Unternehmen');
                }}
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(124,58,237,0.18)',
                  background: 'linear-gradient(180deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.04) 100%)',
                  color: '#6d28d9',
                  '&:hover': {
                    background: 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(139,92,246,0.08) 100%)',
                    transform: 'translateX(1px)',
                  },
                  transition: 'all 0.15s ease',
                }}
                aria-label="go to companies"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={() => handleScopeSelect('Hilfe-Center')} selected={searchScope === 'Hilfe-Center'}>
              <ListItemIcon>
                <HelpOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="Hilfe-Center" />
              <IconButton
                size="small"
                disabled
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(0,0,0,0.08)',
                  color: 'text.disabled',
                }}
                aria-label="go to help center"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => handleScopeSelect('Produkte')} selected={searchScope === 'Produkte'}>
              <ListItemIcon>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText primary="Produkte" />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToScopeLanding('Produkte');
                }}
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(124,58,237,0.18)',
                  background: 'linear-gradient(180deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.04) 100%)',
                  color: '#6d28d9',
                  '&:hover': {
                    background: 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(139,92,246,0.08) 100%)',
                    transform: 'translateX(1px)',
                  },
                  transition: 'all 0.15s ease',
                }}
                aria-label="go to all products"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={() => handleScopeSelect('Angebote')} selected={searchScope === 'Angebote'}>
              <ListItemIcon>
                <LocalOfferIcon />
              </ListItemIcon>
              <ListItemText primary="Angebote" />
              <IconButton
                size="small"
                disabled
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(0,0,0,0.08)',
                  color: 'text.disabled',
                }}
                aria-label="go to offers"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={() => handleScopeSelect('Mitglieder')} selected={searchScope === 'Mitglieder'}>
              <ListItemIcon>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText primary="Mitglieder" />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToScopeLanding('Mitglieder');
                }}
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(124,58,237,0.18)',
                  background: 'linear-gradient(180deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.04) 100%)',
                  color: '#6d28d9',
                  '&:hover': {
                    background: 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(139,92,246,0.08) 100%)',
                    transform: 'translateX(1px)',
                  },
                  transition: 'all 0.15s ease',
                }}
                aria-label="go to members"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={() => handleScopeSelect('Hilfe-Center')} selected={searchScope === 'Hilfe-Center'}>
              <ListItemIcon>
                <HelpOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="Hilfe-Center" />
              <IconButton
                size="small"
                disabled
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(0,0,0,0.08)',
                  color: 'text.disabled',
                }}
                aria-label="go to help center"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
            <MenuItem onClick={() => handleScopeSelect('Nach Ort')} selected={searchScope === 'Nach Ort'}>
              <ListItemIcon>
                <LocationOnIcon />
              </ListItemIcon>
              <ListItemText primary="Nach Ort" />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToScopeLanding('Nach Ort');
                }}
                sx={{
                  ml: 1,
                  width: 28,
                  height: 28,
                  border: '1px solid rgba(124,58,237,0.18)',
                  background: 'linear-gradient(180deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.04) 100%)',
                  color: '#6d28d9',
                  '&:hover': {
                    background: 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(139,92,246,0.08) 100%)',
                    transform: 'translateX(1px)',
                  },
                  transition: 'all 0.15s ease',
                }}
                aria-label="go to map search"
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
          </>
        )}
      </Menu>

      <CartDrawer open={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />

      <FavoritesDrawer open={favoritesDrawerOpen} onClose={() => setFavoritesDrawerOpen(false)} />

      {/* Language Menu */}
      <Menu
        anchorEl={languageAnchorEl}
        open={Boolean(languageAnchorEl)}
        onClose={closeLanguageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            mt: 1,
            minWidth: 280,
            maxWidth: 300,
            boxShadow: "0 10px 30px rgba(2,132,199,0.25)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)",
            "& .MuiMenuItem-root": {
              gap: 1,
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              px: 1.25,
            },
          },
        }}
      >
        {/* Auto-Translation Toggle */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            mb: 0.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={autoTranslate}
                onChange={(e) => {
                  e.stopPropagation();
                  setAutoTranslate(e.target.checked);
                }}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "primary.main",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "primary.main",
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TranslateIcon sx={{ fontSize: 16, color: "primary.main" }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                >
                  {t("translation.autoTranslate")}
                </Typography>
              </Box>
            }
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": {
                flex: 1,
              },
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>

        {/* Language Options */}
        {languageOptions.map((opt) => {
          const isSupported = opt.supported;
          const isSelected = language === opt.code;
          return (
            <MenuItem
              key={opt.code}
              onClick={() => handleLanguageSelect(opt.code)}
              selected={isSelected}
              disabled={!isSupported}
              sx={{
                opacity: isSupported ? 1 : 0.5,
                cursor: isSupported ? "pointer" : "not-allowed",
                "&:hover": {
                  backgroundColor: isSupported
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                },
              }}
            >
              {renderFlag(opt as any, 20, 1)}
              <ListItemText
                primary={opt.label}
                secondary={isSupported ? opt.code.toUpperCase() : "Coming soon"}
                sx={{
                  "& .MuiListItemText-secondary": {
                    color: isSupported ? "text.secondary" : "text.disabled",
                    fontSize: "0.75rem",
                  },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Mobile Search Panel */}
      {isMobile && (
        <MobileSearchPanel
          open={mobileSearchOpen}
          value={searchQuery}
          placeholder={is_job ? t("header.searchJobs") : t("header.searchProducts")}
          onChange={(val) => setSearchQuery(val)}
          onSubmit={() => {
            if (searchQuery.trim()) {
              const searchPath = is_job ? "/all-jobs" : "/all-products";
              navigate(`${searchPath}?search=${encodeURIComponent(searchQuery)}`);
              setMobileSearchOpen(false);
            }
          }}
          onSubmitWithScope={(scope) => {
            if (!searchQuery.trim()) return;
            if (scope === "Nach Ort") {
              const path = is_job ? "/all-jobs" : "/all-products";
              navigate(`${path}?map=1&place=${encodeURIComponent(searchQuery)}`);
              setMobileSearchOpen(false);
            } else {
              const searchPath = is_job ? "/all-jobs" : "/all-products";
              navigate(`${searchPath}?search=${encodeURIComponent(searchQuery)}`);
              setMobileSearchOpen(false);
            }
          }
          }
          onClose={() => setMobileSearchOpen(false)}
        />
      )}

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 18px 60px rgba(2,132,199,0.18)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)',
            backdropFilter: 'blur(10px)',
            '& .MuiMenuItem-root': {
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              px: 1.25,
              py: 1,
              fontWeight: 700,
              gap: 0.75,
              transition: 'all 0.16s ease',
              '&:hover': {
                background: 'linear-gradient(180deg, rgba(2,132,199,0.10) 0%, rgba(2,132,199,0.05) 100%)',
                transform: 'translateY(-1px)',
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 34,
              color: 'text.secondary',
            },
            '& .MuiDivider-root': {
              my: 0.75,
              borderColor: 'rgba(148,163,184,0.35)',
            },
          },
        }}
      >
        {isAuthenticated && (
          <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 900 }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }} noWrap>
                  {user?.name || t('header.profile')}
                </Typography>
                {userEmail ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }} noWrap>
                    {userEmail}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Divider sx={{ mt: 1.25, mb: 0.25 }} />
          </Box>
        )}

        {/* Quick actions on mobile: Favorites and Cart (shop mode only) */}
        {isMobile && !is_job && (
          <>
            <MenuItem
              onClick={() => {
                setFavoritesDrawerOpen(true);
                handleProfileMenuClose();
              }}
            >
              <ListItemIcon>
                <FavoriteIcon />
              </ListItemIcon>
              <ListItemText primary={t ? t("header.favorites") : "Favorites"} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setCartDrawerOpen(true);
                handleProfileMenuClose();
              }}
            >
              <ListItemIcon>
                <Badge badgeContent={totalItems} color="error">
                  <CartIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText primary={t ? t("header.cart") : "Cart"} />
            </MenuItem>
            <Divider />
          </>
        )}
        {userMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => {
              navigate(item.path);
              handleProfileMenuClose();
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
        <MenuItem onClick={handleMessagesClick}>
          <ListItemIcon>
            <MessageIcon />
          </ListItemIcon>
          <ListItemText primary={t("header.messages")} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={t("header.logout")} />
        </MenuItem>
      </Menu>

      {/* Mobile Menu */}
      {renderMobileMenu()}

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        initialTab={authModalTab}
      />

      {/* Messages Sidebar */}
      <MessagesSidebar
        open={messagesSidebarOpen}
        onClose={handleMessagesSidebarClose}
      />

      {/* Categories Dialog (mobile: full-screen, desktop: centered popup) */}
      <Dialog
        open={mobileCategoriesOpen}
        onClose={() => setMobileCategoriesOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            width: isMobile ? '100vw' : { xs: '92vw', sm: 680, md: 860 },
            mx: 'auto',
            maxHeight: isMobile ? '100vh' : '80vh',
            overflow: 'hidden',
          },
        }}
      >
        <AppBar sx={{ position: 'sticky', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
          <Toolbar sx={{ minHeight: 48 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              {t("header.categories")}
            </Typography>
            <IconButton edge="end" onClick={() => setMobileCategoriesOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 1, overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 48px)' : 'calc(80vh - 48px)' }}>
          {Object.keys(defaultMegaMenuData)
            .filter((k) => k.toLowerCase() !== 'default')
            .map((cat) => {
            const catData = (defaultMegaMenuData as any)[cat];
            if (!catData) return null;
            return (
              <Accordion key={cat} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75 }}>
                  {categoryIcons[cat] && (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      {categoryIcons[cat]}
                    </Box>
                  )}
                  <Typography sx={{ fontWeight: 700 }}>{cat}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                  {/* Left items as nested accordions */}
                  {catData.left.map((item: any) => (
                    <Accordion key={`${cat}-${item.key}`} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
                        {item.icon && <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>}
                        <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        {(catData.rightByKey?.[item.key] || []).map((col: any) => (
                          <Box key={`${cat}-${item.key}-${col.title}`} sx={{ mb: 1.25 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>
                              {col.title}
                            </Typography>
                            <List disablePadding>
                              {(col.items || []).map((sub: any) => {
                                const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                const path = typeof sub === 'string'
                                  ? `/all-products?category=${encodeURIComponent(cat)}&sub=${encodeURIComponent(sub)}`
                                  : sub?.path || `/all-products?category=${encodeURIComponent(cat)}&sub=${encodeURIComponent(label)}`;
                                return (
                                  <ListItemButton
                                    key={`${cat}-${item.key}-${col.title}-${label}`}
                                    sx={{ borderRadius: 1, py: 0.75 }}
                                    onClick={() => {
                                      navigate(path);
                                      setMobileCategoriesOpen(false);
                                      setMobileMenuOpen(false);
                                    }}
                                  >
                                    <ListItemText primary={label} />
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Dialog>

      {/* Map Search Modal from Header */}
      <MapSearchNew
        open={mapSearchOpen}
        onClose={() => setMapSearchOpen(false)}
        onLocationSelect={(location) => {
          // Track selected names locally to satisfy component API
          const next = [...headerSelectedMapLocations, location.name];
          setHeaderSelectedMapLocations(next);
        }}
        selectedLocations={headerSelectedMapLocations}
        initialSearchQuery={mapInitialQuery}
        isJobMode={is_job}
      />
    </>
  );
};

export default Header;
